import { SearchType } from '@aibees/elephant/types';
import { ErrorResponse, HttpMethod } from '@aibees/shared/definition';
import {
  BrowseIdsApiOptions,
  BrowseIdsApiSelectionContext,
} from '@aibees/shared/features';
import { useBaseQuery } from '@aibees/shared/http';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import type { UseQueryResult } from '@tanstack/react-query';
import { omit } from 'lodash-es';
import { useDeferredValue, useMemo } from 'react';
import {
  COMPANY_SEARCH_API_METADATA_PREFIX,
  PEOPLE_SEARCH_API_METADATA_PREFIX,
} from '../constants';
import { CompanySearchBody } from '../feature/search-table-company/hooks/types';
import { transform as companyRequestTransform } from '../feature/search-table-company/hooks/use-browse-company-search';
import { transform as peopleRequestTransform } from '../feature/search-table-people/hooks/use-browse-people-search';
import { BrowseSearchIdsRequest } from './types';
import {
  useSearchModeFromRoute,
  useSearchTypeFromRoute,
} from './use-obtain-search-type-from-route';
import { useSearchQueryFilter } from './use-search-query-filter';

export const PEOPLE_COMPANY_MAP_EXTRA_PARAM = 'peopleCompanyMap';

// The id_pair API groups people by company id, but people with no associated
// company are bucketed under this sentinel key. They count as selected people
// (so they stay in the flat id list) but must not contribute a company id.
const UNKNOWN_COMPANY_KEY = 'unknown';

type PeopleIdsResponse = Record<string, string[]>;
type RawIdsResponse = string[] | PeopleIdsResponse;

export function useBrowseSearchIds(
  request: BrowseSearchIdsRequest,
  queryParams?: Record<string, unknown>,
  enabled?: boolean,
  options?: BrowseIdsApiOptions,
  selection?: BrowseIdsApiSelectionContext
): UseQueryResult<string[], ErrorResponse> {
  const workspace = useWorkspace()?.id;
  const { params } = useSearchQueryFilter();
  const deferredParams = useDeferredValue(params);
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();
  const isPeoplePath = type === SearchType.People;
  const url = isPeoplePath
    ? PEOPLE_SEARCH_API_METADATA_PREFIX
    : COMPANY_SEARCH_API_METADATA_PREFIX;

  const reqParams = objSanitizeNilValues(
    omit({ ...request, ...deferredParams }, 'type', 'page', 'size')
  );

  const transformedRequestParams = isPeoplePath
    ? peopleRequestTransform(reqParams)
    : companyRequestTransform(reqParams, mode);

  const requestQueryParams = isPeoplePath ? { return: 'id_pair' } : queryParams;

  const { onSuccess: userOnSuccess, ...restOptions } = options ?? {};

  const result = useBaseQuery<
    CompanySearchBody &
      Omit<BrowseSearchIdsRequest, 'sort'> & { sort?: [string] },
    RawIdsResponse
  >(
    [
      useBrowseSearchIds.name,
      type,
      requestQueryParams,
      {
        ...transformedRequestParams,
        size: request.size,
      },
    ],
    {
      url: url?.replace(':wid', String(workspace)),
      method: HttpMethod.post,
      params: requestQueryParams,
      data: {
        ...transformedRequestParams,
        ...{
          size: request.size,
          page: 0,
        },
      },
    },
    {
      enabled,
      ...restOptions,
      onSuccess: (raw) => {
        if (isPeoplePath && raw && !Array.isArray(raw)) {
          const map: Record<string, string> = {};
          for (const [companyId, personIds] of Object.entries(raw)) {
            if (companyId === UNKNOWN_COMPANY_KEY) continue;
            for (const pid of personIds) map[pid] = companyId;
          }
          selection?.onSetExtraParam?.(PEOPLE_COMPANY_MAP_EXTRA_PARAM, map);
        }
        const flat = Array.isArray(raw) ? raw : Object.values(raw).flat();
        userOnSuccess?.(flat);
      },
    }
  );

  const flatData = useMemo<string[] | undefined>(() => {
    const raw = result.data;
    if (!raw) return undefined;
    return Array.isArray(raw) ? raw : Object.values(raw).flat();
  }, [result.data]);

  return { ...result, data: flatData } as UseQueryResult<
    string[],
    ErrorResponse
  >;
}
